import { GATEWAY_URL } from "../../env";
import request from "../Request";

// The locize webhook publishes two exports to S3, each versioned with its own remote config
// prefix: Localization/{lng}{locize_prefix}.json holds the game client keys and
// LocalizationBackend/{lng}{locize_prefix_be}.json the backend + landing ones. Merging both is
// what makes every locize key available here.
const S3_BASE_URL = "https://frontend.assets.tangelogames.app";
const CLIENT_FOLDER = "/skills/Localization/";
const BACKEND_FOLDER = "/skills/LocalizationBackend/";

const LOCIZE_PREFIX_KEY = "locize_prefix";

// Bump the version segment to invalidate every cached entry at once.
const CACHE_KEY_PREFIX = "landing.localization.v1.";
const PREFIXES_CACHE_KEY = `${CACHE_KEY_PREFIX}prefixes`;

class LocalizationRepository {
    static _prefixesPromise = null;
    static _stringsPromises = new Map();

    static getPrefixes() {
        if (!LocalizationRepository._prefixesPromise) {
            LocalizationRepository._prefixesPromise = LocalizationRepository._loadPrefixes();
        }

        return LocalizationRepository._prefixesPromise;
    }

    static getStrings(language) {
        if (!LocalizationRepository._stringsPromises.has(language)) {
            const promise = LocalizationRepository._loadStrings(language).then((strings) => {
                // Forget an empty result so a later call can try to download again.
                if (!strings || Object.keys(strings).length === 0) {
                    LocalizationRepository._stringsPromises.delete(language);
                }

                return strings;
            });

            LocalizationRepository._stringsPromises.set(language, promise);
        }

        return LocalizationRepository._stringsPromises.get(language);
    }

    static clearCache() {
        const storage = LocalizationRepository._getStorage();
        if (!storage) {
            return;
        }

        try {
            Object.keys(storage)
                .filter((key) => key.startsWith(CACHE_KEY_PREFIX))
                .forEach((key) => storage.removeItem(key));
        } catch (error) {
            console.warn("[LocalizationRepository] Could not clear the cache:", error);
        }
    }

    static async _loadPrefixes() {
        const [client, backend] = await Promise.all([
            LocalizationRepository._getClientPrefix(),
            LocalizationRepository._getBackendPrefix(),
        ]);

        // null means the request failed (an empty prefix is a valid value pointing to the
        // unversioned file), so the last known good pair is reused to keep hitting the files
        // already cached instead of the stale unversioned ones.
        const cached = LocalizationRepository._readJson(PREFIXES_CACHE_KEY);
        const prefixes = {
            client: client ?? cached?.client ?? "",
            backend: backend ?? cached?.backend ?? "",
        };

        if (client !== null && backend !== null) {
            LocalizationRepository._writeJson(PREFIXES_CACHE_KEY, prefixes);
        }

        console.log("[LocalizationRepository] Locize prefixes:", prefixes);
        return prefixes;
    }

    static async _getClientPrefix() {
        try {
            const response = await request.POST(`${GATEWAY_URL}/Config/Get`);
            const prefix = response?.[LOCIZE_PREFIX_KEY];
            return typeof prefix === "string" ? prefix : null;
        } catch (error) {
            console.error("[LocalizationRepository] Error getting the client locize prefix:", error);
            return null;
        }
    }

    static async _getBackendPrefix() {
        try {
            const prefix = await request.GET(`${GATEWAY_URL}/Config/GetLocizeBEPrefix`);
            return typeof prefix === "string" ? prefix : null;
        } catch (error) {
            console.error("[LocalizationRepository] Error getting the backend locize prefix:", error);
            return null;
        }
    }

    static async _loadStrings(language) {
        const prefixes = await LocalizationRepository.getPrefixes();
        const signature = `${prefixes.client}|${prefixes.backend}`;
        const cacheKey = `${CACHE_KEY_PREFIX}${language}`;
        const cached = LocalizationRepository._readJson(cacheKey);

        if (cached?.signature === signature && cached.strings) {
            console.log(
                `[LocalizationRepository] '${language}' loaded from cache with ${Object.keys(cached.strings).length} keys`
            );
            return cached.strings;
        }

        const [clientStrings, backendStrings] = await Promise.all([
            LocalizationRepository._download(CLIENT_FOLDER, language, prefixes.client),
            LocalizationRepository._download(BACKEND_FOLDER, language, prefixes.backend),
        ]);

        if (!clientStrings && !backendStrings) {
            console.error(`[LocalizationRepository] Could not download any localization file for '${language}'`);
            return cached?.strings ?? {};
        }

        // Backend keys last: they are the ones the landing owns, so they win any collision.
        const strings = { ...clientStrings, ...backendStrings };

        // Caching a partial merge would freeze it under a matching signature and never refresh.
        if (clientStrings && backendStrings) {
            LocalizationRepository._writeJson(cacheKey, { signature, strings });
        }

        console.log(`[LocalizationRepository] '${language}' downloaded with ${Object.keys(strings).length} keys`);
        return strings;
    }

    static async _download(folder, language, prefix) {
        const urls = [`${S3_BASE_URL}${folder}${language}${prefix}.json`];

        // Same retry the Unity client does when the prefixed file is not published yet.
        if (prefix) {
            urls.push(`${S3_BASE_URL}${folder}${language}.json`);
        }

        for (const url of urls) {
            const strings = await LocalizationRepository._fetchJson(url);
            if (strings) {
                return strings;
            }
        }

        return null;
    }

    // Plain fetch on purpose: the bucket allows any origin for GET but no custom request
    // headers, so going through Request (which adds the session headers) would turn this into a
    // preflighted request and fail CORS.
    static async _fetchJson(url) {
        try {
            const response = await fetch(url, { method: "GET" });

            if (!response.ok) {
                console.warn(`[LocalizationRepository] ${url} responded ${response.status}`);
                return null;
            }

            const json = await response.json();
            return json && typeof json === "object" ? json : null;
        } catch (error) {
            console.error(`[LocalizationRepository] Error downloading ${url}:`, error);
            return null;
        }
    }

    static _getStorage() {
        try {
            return typeof window !== "undefined" ? window.localStorage : null;
        } catch (error) {
            // Storage can be disabled by the browser (private mode, blocked cookies, ...).
            return null;
        }
    }

    static _readJson(key) {
        const storage = LocalizationRepository._getStorage();
        if (!storage) {
            return null;
        }

        try {
            const raw = storage.getItem(key);
            return raw ? JSON.parse(raw) : null;
        } catch (error) {
            console.warn(`[LocalizationRepository] Could not read '${key}' from the cache:`, error);
            return null;
        }
    }

    static _writeJson(key, value) {
        const storage = LocalizationRepository._getStorage();
        if (!storage) {
            return;
        }

        const serialized = JSON.stringify(value);

        try {
            storage.setItem(key, serialized);
        } catch (error) {
            // Most likely the quota: drop our own entries and try once more.
            console.warn(`[LocalizationRepository] Could not cache '${key}', retrying after clearing:`, error);
            LocalizationRepository.clearCache();

            try {
                storage.setItem(key, serialized);
            } catch (retryError) {
                console.warn(`[LocalizationRepository] Giving up on caching '${key}':`, retryError);
            }
        }
    }
}

export default LocalizationRepository;
